/**
 * HelixCompositeBadge.tsx — 32×32 compact helix indicator
 *
 * Shows composite M-PACT score in center with 10 colored dots around the edge
 * for CP1–CP10 (red/amber/green). Click → navigates to account scorecard Helix tab.
 *
 * Data: reads from localStorage key `aip:helix:{accountId}` and re-reads on
 * `aip:answer-saved` event for live cross-view sync.
 *
 * Brand: Black #0B0B0F, Red #DC2626, AIP bg #FAFAF7
 */

import { useEffect, useState, useCallback } from "react";
import {
  HELIX_CHECKPOINTS,
  type CheckpointStatus,
} from "@/lib/helix-checkpoints";
import {
  ALL_QUESTIONS,
} from "@shared/questions";
import { getHealthColor } from "@shared/schema";
import { supabase } from "@/lib/session";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface CpAnswer {
  selectedOption: string | null;
  notes: string;
}

type AnswerStore = Record<string, CpAnswer>;

// ─── Helpers ────────────────────────────────────────────────────────────────────

function loadAnswers(accountId: string): AnswerStore {
  try {
    const raw = localStorage.getItem(`aip:helix:${accountId}`);
    if (!raw) return {};
    return JSON.parse(raw) as AnswerStore;
  } catch {
    return {};
  }
}

/**
 * Bank-driven point lookup.
 * Each question's points come from ALL_QUESTIONS[key].options[selectedOption].points.
 * Denominator = max points for the questions the user actually answered.
 * Supports both legacy numeric strings ("0"."4") and modern semantic option keys.
 */
function pointsFor(questionKey: string, selectedOption: string | null | undefined): { got: number; max: number } | null {
  if (selectedOption === null || selectedOption === undefined) return null;
  const q = ALL_QUESTIONS.find((qq) => qq.key === questionKey);
  if (!q) return null;
  const max = Math.max(...q.options.map((o) => o.points));
  // Try semantic key match first
  const opt = q.options.find((o) => o.key === selectedOption);
  if (opt) return { got: Math.max(0, Math.min(opt.points, max)), max };
  // Legacy fallback: numeric string. Clamp to [0, max] so a stale stored
  // value of e.g. "5" cannot push got/max above 1.0 and produce 125/100.
  const n = parseInt(selectedOption, 10);
  if (!isNaN(n)) {
    const effectiveMax = Math.max(max, 4);
    return { got: Math.max(0, Math.min(n, effectiveMax)), max: effectiveMax };
  }
  return null;
}

function computeCpPct(cpNumber: number, answers: AnswerStore): number {
  const keys = ALL_QUESTIONS.filter((q) => q.cp === cpNumber).map((q) => q.key);
  let got = 0, max = 0, count = 0;
  for (const key of keys) {
    const p = pointsFor(key, answers[key]?.selectedOption);
    if (!p) continue;
    got += p.got; max += p.max; count++;
  }
  if (count === 0 || max === 0) return -1;
  // Clamp final pct to [0, 100] so no scoring path can render 133/100.
  return Math.max(0, Math.min(100, Math.round((got / max) * 100)));
}

/**
 * Wave 1 weighted-CP composite — mirrors server-side recompute_account_score().
 *
 * For each CP 1..10:
 *   cp_pct = SUM(points answered) / SUM(max-of-options for answered questions) * 100
 *   If no questions answered in CP, cp_pct contributes 0 to numerator.
 * composite = SUM(cp_pct * 0.10) / 1.0
 *
 * Equal 0.10 weight per CP — Wave 2 swaps these for canonical dimension weights.
 * Empty CPs consume their weight share, so partial coverage is honestly reflected.
 */
function computeCompositePct(answers: AnswerStore): number {
  const CP_WEIGHT = 0.1;
  const TOTAL_WEIGHT = 1.0;
  let weightedSum = 0;

  for (let cp = 1; cp <= 10; cp++) {
    const cpKeys = ALL_QUESTIONS.filter((q) => q.cp === cp).map((q) => q.key);
    let cpGot = 0, cpMax = 0;
    for (const key of cpKeys) {
      const p = pointsFor(key, answers[key]?.selectedOption);
      if (!p) continue;
      cpGot += p.got;
      cpMax += p.max;
    }
    if (cpMax > 0) {
      const cpPct = (cpGot / cpMax) * 100;
      weightedSum += cpPct * CP_WEIGHT;
    }
  }

  const composite = weightedSum / TOTAL_WEIGHT;
  return Math.max(0, Math.min(100, Math.round(composite)));
}

// Composite score → canon palette per CANON.md §5 (@shared/schema).
function scoreColor(pct: number): string {
  return getHealthColor(pct);
}

function deriveStatusFromPct(pct: number): CheckpointStatus {
  if (pct >= 70) return "cleared";
  if (pct >= 40) return "partial";
  return "open";
}

// ─── Props ─────────────────────────────────────────────────────────────────────

export interface HelixCompositeBadgeProps {
  accountId: string;
  /** Optional industry passed for scoring context */
  industry?: string;
  /** If true, renders 48×48 instead of 32×32 */
  size?: "sm" | "md";
  /** Override click behavior (default: navigate to account helix tab) */
  onClick?: () => void;
  /**
   * D-053: When provided, use this pre-fetched composite score (0-100) instead
   * of computing it from localStorage. This ensures the accounts list shows the
   * same score as account detail (sourced from account_answers in the DB) rather
   * than showing 0 when localStorage is not yet hydrated.
   */
  preloadedComposite?: number;
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function HelixCompositeBadge({
  accountId,
  industry,
  size = "sm",
  onClick,
  preloadedComposite,
}: HelixCompositeBadgeProps) {
  const [answers, setAnswers] = useState<AnswerStore>(() => loadAnswers(accountId));

  // Re-read on every aip:answer-saved event (cross-view sync)
  useEffect(() => {
    function handleSync(e: Event) {
      const evt = e as CustomEvent<{ accountId: string }>;
      if (evt.detail?.accountId === accountId) {
        setAnswers(loadAnswers(accountId));
      }
    }
    window.addEventListener("aip:answer-saved", handleSync);
    return () => window.removeEventListener("aip:answer-saved", handleSync);
  }, [accountId]);

  // Re-read when accountId changes
  useEffect(() => {
    setAnswers(loadAnswers(accountId));
  }, [accountId]);

  // DB hydration: when localStorage is empty for this account, pull answers
  // from account_answers and seed localStorage. Added 2026-05-15.
  //
  // D-085 (2026-05-22): Skip hydration when preloadedComposite is provided.
  // Sentry caught an N+1 — list/manager views render this badge per account and
  // each one fired its own .eq('account_id', N) query on cold loads (no
  // localStorage). With preloadedComposite the composite number is already
  // correct; per-CP donut detail can stay unhydrated until the user opens the
  // account. This eliminates the N+1 entirely on list and manager pages.
  useEffect(() => {
    if (preloadedComposite !== undefined) return;
    const accountIdNum = parseInt(accountId, 10);
    if (Number.isNaN(accountIdNum)) return;
    const stored = loadAnswers(accountId);
    if (Object.keys(stored).length > 0) return;
    let cancelled = false;
    supabase()
      .from("account_answers")
      .select("question_key, selected_option, answer_notes, updated_at")
      .eq("account_id", accountIdNum)
      .then(({ data, error }) => {
        if (cancelled || error || !data || data.length === 0) return;
        const hydrated: AnswerStore = {};
        for (const row of data) {
          if (!row.question_key) continue;
          hydrated[row.question_key] = {
            selectedOption: row.selected_option ?? null,
            notes: row.answer_notes ?? "",
          };
        }
        if (Object.keys(hydrated).length > 0) {
          try { localStorage.setItem(`aip:helix:${accountId}`, JSON.stringify(hydrated)); } catch {}
          setAnswers(hydrated);
        }
      });
    return () => { cancelled = true; };
  }, [accountId, preloadedComposite]);

  // Compute per-CP scores using 5-point math
  const cpData = HELIX_CHECKPOINTS.map((cp) => {
    const pct = computeCpPct(cp.number, answers);
    const status = deriveStatusFromPct(pct < 0 ? 0 : pct);
    return { cp, pct, status };
  });

  // D-053: Use preloaded composite from the server response when available
  // (e.g., the accounts list already has the correct score from account_answers).
  // Fall back to localStorage-computed value for detail views.
  const composite = preloadedComposite !== undefined ? Math.round(preloadedComposite) : computeCompositePct(answers);
  const compositeColor = scoreColor(composite);

  const dim = size === "md" ? 48 : 32;
  const center = dim / 2;
  const dotRadius = size === "md" ? 3.5 : 2.2;
  // Ring radius: leave room for dots
  const ringR = center - dotRadius - 2;

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    } else {
      window.location.hash = `/account/${accountId}`;
    }
  }, [onClick, accountId]);

  return (
    <button
      onClick={handleClick}
      title={`Composite score: ${composite}/100 — click to open Helix`}
      style={{
        width: dim,
        height: dim,
        borderRadius: "50%",
        background: "#0B0B0F",
        border: `2px solid ${compositeColor}33`,
        padding: 0,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        flexShrink: 0,
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        boxShadow: `0 0 8px ${compositeColor}22`,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = compositeColor;
        (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 12px ${compositeColor}55`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = `${compositeColor}33`;
        (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 8px ${compositeColor}22`;
      }}
      aria-label={`Helix score ${composite}`}
    >
      <svg
        width={dim}
        height={dim}
        viewBox={`0 0 ${dim} ${dim}`}
        style={{ position: "absolute", inset: 0 }}
      >
        {/* 10 dots for CP1–CP10 around the edge */}
        {cpData.map(({ cp, status, pct: P }, i) => {
          const angle = (i / 10) * 2 * Math.PI - Math.PI / 2; // start at top
          const dotX = center + ringR * Math.cos(angle);
          const dotY = center + ringR * Math.sin(angle);
          const color =
            status === "cleared" ? "#16A34A" :
            status === "partial" ? "#EA580C" :
            "#DC2626";
          // Dot is lit if any question for this CP has been answered
          const isAnswered = P > 0 || Object.keys(answers).some(
            (key) => answers[key].selectedOption &&
              ALL_QUESTIONS.find((q) => q.key === key && q.cp === cp.number)
          );

          return (
            <circle
              key={cp.id}
              cx={dotX}
              cy={dotY}
              r={dotRadius}
              fill={isAnswered ? color : "#0B0B0F"}
              stroke={isAnswered ? color : "#3F3F44"}
              strokeWidth={0.8}
              opacity={isAnswered ? 1 : 0.5}
            />
          );
        })}
      </svg>

      {/* Score in center */}
      <span
        style={{
          fontSize: size === "md" ? 13 : 9,
          fontWeight: 800,
          color: compositeColor,
          fontFamily: "Inter, sans-serif",
          lineHeight: 1,
          letterSpacing: "-0.02em",
          position: "relative",
          zIndex: 1,
        }}
      >
        {composite}
      </span>
    </button>
  );
}
